/**
 * Where a visitor came from (IMPLEMENTATION.md R9 a): the campaign parameters, the referrer and
 * the first path the browser opened, remembered from the first visit and sent once with the
 * sign-up, so a company can be traced to the campaign that brought it. First touch wins: a later
 * visit with other parameters does not overwrite it. Nothing here is personal beyond what the
 * URL and the referrer carried; nothing is sent anywhere until sign-up.
 */

export interface Acquisition {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
  referrer?: string;
  landing?: string;
  firstSeenAt?: string;
  /** The browser's visitor id (visitor.ts), added at sign-up so the anonymous visit joins the company. */
  visitor?: string;
}

const KEY = 'paige.acquisition';
const MAX = 200;

const utm: [keyof Acquisition, string][] = [
  ['source', 'utm_source'],
  ['medium', 'utm_medium'],
  ['campaign', 'utm_campaign'],
  ['term', 'utm_term'],
  ['content', 'utm_content'],
];

const clip = (s: string | null | undefined): string | undefined => {
  const t = (s ?? '').trim();
  return t ? t.slice(0, MAX) : undefined;
};

/** What this visit says about where it came from; `ref` stands in for utm_source when that is absent. */
export function acquisitionFrom(url: URL, referrer: string, now: Date): Acquisition {
  const out: Acquisition = {};
  for (const [key, param] of utm) {
    const value = clip(url.searchParams.get(param));
    if (value) out[key] = value;
  }
  if (!out.source) {
    const ref = clip(url.searchParams.get('ref'));
    if (ref) out.source = ref;
  }
  const from = clip(referrer);
  if (from && !sameSite(from, url)) out.referrer = from;
  out.landing = clip(url.pathname) ?? '/';
  out.firstSeenAt = now.toISOString();
  return out;
}

function sameSite(referrer: string, url: URL): boolean {
  try {
    return new URL(referrer).host === url.host;
  } catch {
    return false;
  }
}

/** Remembers the first visit; a visit that already has one changes nothing. */
export function captureAcquisition(): void {
  try {
    if (localStorage.getItem(KEY)) return;
    const seen = acquisitionFrom(new URL(window.location.href), document.referrer, new Date());
    localStorage.setItem(KEY, JSON.stringify(seen));
  } catch {
    /* a private window keeps nothing; sign-up then carries no attribution, which is honest */
  }
}

export function storedAcquisition(): Acquisition | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Acquisition) : null;
  } catch {
    return null;
  }
}

/** After the sign-up carried it: the record belongs to the company now, not the browser. */
export function clearAcquisition(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}
